export type BrowserAnalysisReason =
  | 'unsupported_browser'
  | 'wasm_load_failed'
  | 'engine_init_timeout'
  | 'worker_crash'
  | 'all_positions_timeout'
  | 'oom'
  | 'no_pgn'
  | 'empty_pgn'
  | 'persist_failed'
  | 'cancelled'
  | 'unknown';

export interface FailureStats {
  positionsTotal: number;
  positionsTimedOut: number;
}

export const ALL_POSITIONS_TIMEOUT_RATIO = 0.5;

function messageOf(err: unknown): string {
  if (err instanceof Error) {
    return `${err.name}: ${err.message}`.toLowerCase();
  }
  if (typeof err === 'string') {
    return err.toLowerCase();
  }
  return '';
}

// Note: `cancelled` is never returned here — the analysis caller sets it
// directly when the user aborts. classifyFailure only maps real errors.
export function classifyFailure(
  err: unknown,
  stats?: FailureStats,
): BrowserAnalysisReason {
  const msg = messageOf(err);
  if (!msg && !stats) {
    return 'unknown';
  }
  if (msg.includes('webassembly') && msg.includes('not supported')) {
    return 'unsupported_browser';
  }
  // OOM — prefer the structured signal over substrings.
  if (err instanceof RangeError) {
    return 'oom';
  }
  if (
    msg.includes('out of memory') ||
    msg.includes('oom') ||
    msg.includes('cannot enlarge memory')
  ) {
    return 'oom';
  }
  if (msg.includes('no pgn')) {
    return 'no_pgn';
  }
  if (msg.includes('produced no moves') || msg.includes('empty pgn')) {
    return 'empty_pgn';
  }
  if (msg.includes('persist')) {
    return 'persist_failed';
  }
  // WASM load: require a wasm/stockfish/webassembly context so generic
  // "load"/"fetch" (e.g. a worker failing to load) don't get bucketed here.
  const wasmCtx =
    msg.includes('.wasm') ||
    msg.includes('wasm') ||
    msg.includes('stockfish') ||
    msg.includes('webassembly');
  if (
    msg.includes('instantiate') ||
    (wasmCtx &&
      (msg.includes('load') ||
        msg.includes('fetch') ||
        msg.includes('compile')))
  ) {
    return 'wasm_load_failed';
  }
  // Engine UCI handshake timeout — specific tokens only (not bare "init").
  if (
    msg.includes('uciok') ||
    msg.includes('readyok') ||
    (msg.includes('engine') && msg.includes('init'))
  ) {
    return 'engine_init_timeout';
  }
  if (
    stats &&
    stats.positionsTotal > 0 &&
    stats.positionsTimedOut / stats.positionsTotal >=
      ALL_POSITIONS_TIMEOUT_RATIO
  ) {
    return 'all_positions_timeout';
  }
  if (msg) {
    return 'worker_crash';
  }
  return 'unknown';
}
